import { useState, useEffect } from 'react';
import { ChevronDown, ExternalLink, RefreshCw, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';
import PipelineBox from './PipelineBox';
import { edgeService, suricataService } from '../../services';
import type { RoutingPolicy } from '../../services';
import api from '../../services/api';
import { isSystemAcl } from '../../utils/aclUtils';

interface PolicyPipelineDiagramProps {
  edgeId: string;
  tenantId?: string;
  assignedPolicies: RoutingPolicy[];
}

interface PipelineData {
  edge: {
    vppMode?: string;
    devName?: string;
  } | null;
  suricata: {
    enabled: boolean;
    mode: string;
    service_chain_enabled: boolean;
  } | null;
  aclCount: { user: number; system: number };
  staticRouteCount: number;
}

const PolicyPipelineDiagram = ({ edgeId, assignedPolicies }: PolicyPipelineDiagramProps) => {
  const [data, setData] = useState<PipelineData>({
    edge: null,
    suricata: null,
    aclCount: { user: 0, system: 0 },
    staticRouteCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    fetchPipelineData();
  }, [edgeId]);

  const fetchPipelineData = async () => {
    try {
      setLoading(true);
      const [edgeRes, suricataRes, aclStatsRes, routeRes] = await Promise.allSettled([
        edgeService.getById(edgeId),
        suricataService.getConfig(edgeId),
        api.get(`/api/v2/edges/${edgeId}/acl/stats`),
        edgeService.listStaticRoutes(edgeId),
      ]);

      const edge = edgeRes.status === 'fulfilled' ? edgeRes.value : null;
      const suricataConfig = suricataRes.status === 'fulfilled' ? suricataRes.value : null;
      const aclStats = aclStatsRes.status === 'fulfilled' ? (aclStatsRes.value?.data?.data || aclStatsRes.value?.data) : null;
      const aclList = aclStats?.acls || [];
      const routes = routeRes.status === 'fulfilled' ? (routeRes.value as any[]) : [];

      // Count ACL rules from VPP stats (includes both user and system ACLs)
      let userRules = 0;
      let systemRules = 0;
      if (Array.isArray(aclList)) {
        aclList.forEach((acl: any) => {
          const rules = acl.rulesCount || 0;
          if (isSystemAcl(acl.aclName || '')) {
            systemRules += rules;
          } else {
            userRules += rules;
          }
        });
      }

      setData({
        edge: edge ? { vppMode: (edge as any).vppMode || (edge as any).mode, devName: (edge as any).devName } : null,
        suricata: suricataConfig?.config ? {
          enabled: !!suricataConfig.config.enabled,
          mode: suricataConfig.config.mode || 'ids',
          service_chain_enabled: !!suricataConfig.config.service_chain_enabled,
        } : null,
        aclCount: { user: userRules, system: systemRules },
        staticRouteCount: Array.isArray(routes) ? routes.length : 0,
      });
    } catch (error) {
      console.error('Failed to fetch pipeline data:', error);
    } finally {
      setLoading(false);
    }
  };

  const isDPDK = data.edge?.vppMode?.toLowerCase()?.includes('dpdk');

  // Compute pipeline step data
  const suricataEnabled = data.suricata?.enabled && data.suricata?.service_chain_enabled;
  const suricataRuleCount = assignedPolicies
    .flatMap(p => p.rules || [])
    .filter(r => r.requires_suricata === 1).length;

  const activePolicies = assignedPolicies.filter(p => p.enabled === 1);
  const totalPolicyRules = activePolicies.reduce((acc, p) => acc + (p.rules?.length || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-16">
        <RefreshCw className="w-4 h-4 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="mb-6">
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="flex items-center space-x-2 text-sm font-medium text-gray-600 hover:text-gray-900 mb-3"
      >
        <ChevronDown className={`w-4 h-4 transition-transform ${collapsed ? '-rotate-90' : ''}`} />
        <span>Traffic Processing Pipeline</span>
      </button>

      {!collapsed && (
        <div className="space-y-1">
          {/* Traffic Sources */}
          <div className="text-center py-1.5">
            <span className="text-xs text-gray-400">
              Traffic Sources: G0 (WAN) &middot; G1-G4 (LAN) &middot; wg0 &middot; wg1 &middot; wg2
            </span>
          </div>

          <Arrow />

          {/* Step 1: IKEv2 NAT-T Bypass */}
          <PipelineBox
            step={1}
            label="IKEv2 NAT-T Bypass"
            detail="UDP 4500 → StrongSwan kernel xfrm"
            priority="5"
            badges={isDPDK ? ['G0 only', 'DPDK only'] : ['G0 only']}
            status={isDPDK ? 'conditional' : 'disabled'}
            color="purple"
          />

          <Arrow />

          {/* Step 2: Tunnel Protection */}
          <PipelineBox
            step={2}
            label="Tunnel Protection"
            detail="Exempts ESP, WireGuard, IKE from Suricata inspection"
            priority="8"
            badges={['Physical interfaces only']}
            status={suricataEnabled ? 'active' : 'disabled'}
            color="red"
          />

          <Arrow />

          {/* Step 3: ACL Rules */}
          <PipelineBox
            step={3}
            label="ACL Rules"
            detail={
              data.aclCount.user + data.aclCount.system > 0
                ? `${data.aclCount.user} user rules, ${data.aclCount.system} system rules`
                : 'No ACL rules configured'
            }
            badges={['Permit / Deny']}
            status={data.aclCount.user + data.aclCount.system > 0 ? 'active' : 'empty'}
            color="orange"
            onClick={() => {
              // Navigate to ACL tab
              const el = document.querySelector('[data-tab="acl"]');
              if (el instanceof HTMLElement) el.click();
            }}
          >
            <Link
              to={`/edges/${edgeId}/config`}
              className="text-xs text-blue-600 hover:text-blue-800 inline-flex items-center"
            >
              <Settings className="w-3 h-3 mr-1" /> Configure ACLs
              <ExternalLink className="w-3 h-3 ml-1" />
            </Link>
          </PipelineBox>

          <Arrow />

          {/* Step 4: Routing Policies */}
          <PipelineBox
            step={4}
            label="Routing Policies"
            detail={
              activePolicies.length > 0
                ? `${activePolicies.length} active ${activePolicies.length === 1 ? 'policy' : 'policies'}, ${totalPolicyRules} rules`
                : 'No active routing policies'
            }
            priority="20-99"
            badges={suricataRuleCount > 0 ? [`${suricataRuleCount} require Suricata`] : undefined}
            status={activePolicies.length > 0 ? 'active' : 'empty'}
            color="blue"
          >
            <div className="space-y-1.5">
              {activePolicies.length > 0 ? (
                activePolicies.map(p => (
                  <div key={p.id} className="flex items-center justify-between text-xs">
                    <div className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                      <span className="text-gray-700 font-medium">{p.name}</span>
                      <span className="text-gray-400">P:{p.priority}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-400">{p.rules?.length || 0} rules</span>
                      {(p.rules || []).some(r => r.requires_suricata === 1) && (
                        <span className="px-1 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px]">Suricata</span>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-xs text-gray-400">
                  No routing policies assigned. Traffic continues to Suricata and FIB lookup.
                </p>
              )}
            </div>
          </PipelineBox>

          <Arrow />

          {/* Step 5: Suricata IDS/IPS */}
          <PipelineBox
            step={5}
            label="Suricata IDS/IPS"
            detail={
              suricataEnabled
                ? `Mode: ${data.suricata?.mode?.toUpperCase()} — tap100 → inspect → tap101`
                : 'Suricata service chain disabled'
            }
            priority="100"
            badges={suricataEnabled ? ['All interfaces'] : undefined}
            status={suricataEnabled ? 'active' : 'disabled'}
            color="green"
          >
            <div className="space-y-1">
              {suricataEnabled ? (
                <>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">DSCP Classification</span>
                    <span className="text-gray-400">{suricataRuleCount} rules with dscp_mark</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">Domain Detection</span>
                    <span className="text-gray-400">
                      {assignedPolicies.flatMap(p => p.rules || []).filter(r => r.requires_suricata === 1).length > 0 ? 'Active' : 'None'}
                    </span>
                  </div>
                </>
              ) : (
                <p className="text-xs text-gray-400">
                  Domain-based detection and DSCP marking unavailable when disabled.
                </p>
              )}
              <Link
                to={`/edges/${edgeId}/config`}
                className="text-xs text-blue-600 hover:text-blue-800 inline-flex items-center mt-1"
              >
                <Settings className="w-3 h-3 mr-1" /> Configure Suricata
                <ExternalLink className="w-3 h-3 ml-1" />
              </Link>
            </div>
          </PipelineBox>

          <Arrow />

          {/* Step 6: FIB Lookup */}
          <PipelineBox
            step={6}
            label="FIB Lookup"
            detail={
              data.staticRouteCount > 0
                ? `${data.staticRouteCount} static routes + connected + default`
                : 'Connected networks + default route'
            }
            status="active"
            color="gray"
          >
            <Link
              to={`/edges/${edgeId}/config`}
              className="text-xs text-blue-600 hover:text-blue-800 inline-flex items-center"
            >
              <Settings className="w-3 h-3 mr-1" /> Manage Routes
              <ExternalLink className="w-3 h-3 ml-1" />
            </Link>
          </PipelineBox>
        </div>
      )}
    </div>
  );
};

const Arrow = ({ label }: { label?: string }) => (
  <div className="flex flex-col items-center py-0.5">
    <ChevronDown className="w-4 h-4 text-gray-300" />
    {label && <span className="text-[10px] text-gray-400 -mt-1">{label}</span>}
  </div>
);

export default PolicyPipelineDiagram;
